import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, get, update } from 'firebase/database';
import { db, auth } from '../Config/firebase';
import { signOut } from 'firebase/auth';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import { useUser } from '../Contexts/UserContext';
import { FaEdit, FaTrophy, FaGamepad, FaClock } from 'react-icons/fa';
import { isUsernameAvailable } from '../Components/userUtils';

const Profile = () => {
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const { user, setUser } = useUser();
    const [isEditing, setIsEditing] = useState(false);
    const [userData, setUserData] = useState({
        username: '',
        email: '',
        gamesPlayed: 0,
        highScore: 0,
        totalPlayTime: 0,
        favoriteMap: 'Farm',
        chickensChased: 0,
        achievements: [],
        joinDate: ''
    });
    const [editedUsername, setEditedUsername] = useState('');

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.uid) {
        navigate('/signin');
        return;
      }

      try {
        const userRef = ref(db, 'users/' + user.uid);
        const snapshot = await get(userRef);
        if (snapshot.exists()) {
          const data = snapshot.val();
          setUserData({
            ...data,
            email: user.email,
            gamesPlayed: data.gamesPlayed || 0,
            highScore: data.highScore || 0,
            totalPlayTime: data.totalPlayTime || 0,
            favoriteMap: data.favoriteMap || 'Farm',
            chickensChased: data.chickensChased || 0,
            achievements: data.achievements || [],
            joinDate: data.joinDate || new Date().toISOString()
          });
          setEditedUsername(data.username);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, [user, navigate]);

  const handleUsernameUpdate = async () => {
    if (!editedUsername.trim()) return;

    try {
      const available = await isUsernameAvailable(editedUsername, user.uid);
      
      if (!available) {
        setError('Username already taken. Please choose another.');
        return;
      }

      await update(ref(db, `users/${user.uid}`), {
        username: editedUsername
      });
      setUserData(prev => ({ ...prev, username: editedUsername }));
      setUser(prev => ({ ...prev, username: editedUsername }));
      setIsEditing(false);
      setError('');
    } catch (error) {
      console.error('Error updating username:', error);
      setError('Failed to update username. Please try again.');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setUser(null);
      navigate('/signin');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const formatPlayTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  return (
    <div>
        <Navbar />
        <section className="min-h-screen bg-white flex items-center pt-24 pb-16">
            <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                    {/* Profile Header */}
                    <div className="text-center mb-12">
                        <div className="w-32 h-32 mx-auto bg-[#E57373] rounded-full flex items-center justify-center mb-4">
                            <span className="text-4xl text-white font-bold">
                                {userData.username?.charAt(0)?.toUpperCase()}
                            </span>
                        </div>
                        <div className="relative inline-block">
                            {isEditing ? (
                                <div className="flex flex-col items-center justify-center gap-2">
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={editedUsername}
                                            onChange={(e) => setEditedUsername(e.target.value)}
                                            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-[#E57373]"
                                        />
                                        <button
                                            onClick={handleUsernameUpdate}
                                            className="bg-[#E57373] text-white px-4 py-2 rounded-md hover:bg-[#EF9A9A]"
                                        >
                                            Save
                                        </button>
                                    </div>
                                    {error && (
                                        <p className="text-red-500 text-sm mt-2">{error}</p>
                                    )}
                                </div>
                            ) : (
                                <h1 className="text-6xl font-medium text-gray-800 font-trends inline-block">
                                    {userData.username}
                                    <button
                                        onClick={() => setIsEditing(true)}
                                        className="ml-3 text-[#E57373] hover:text-[#EF9A9A]"
                                    >
                                        <FaEdit size={24} />
                                    </button>
                                </h1>
                            )}
                        </div>
                        <p className="text-gray-600 mt-2">{userData.email}</p>
                        <p className="text-sm text-gray-500">
                            Joined {new Date(userData.joinDate).toLocaleDateString()}
                        </p>
                    </div>


            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                <div className="flex items-center mb-4">
                  <FaGamepad className="text-[#E57373] mr-2" size={24} />
                  <h3 className="text-xl font-semibold">Games Played</h3>
                </div>
                <p className="text-3xl font-bold text-gray-800">{userData.gamesPlayed}</p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                <div className="flex items-center mb-4">
                  <FaTrophy className="text-[#E57373] mr-2" size={24} />
                  <h3 className="text-xl font-semibold">High Score</h3>
                </div>
                <p className="text-3xl font-bold text-gray-800">{userData.highScore}</p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                <div className="flex items-center mb-4">
                  <FaClock className="text-[#E57373] mr-2" size={24} />
                  <h3 className="text-xl font-semibold">Play Time</h3>
                </div>
                <p className="text-3xl font-bold text-gray-800">
                  {formatPlayTime(userData.totalPlayTime)}
                </p>
              </div>
            </div>

            {/* Achievements Section */}
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200 mb-8">
              <h2 className="text-2xl font-semibold mb-4">Achievements</h2>
              {userData.achievements && userData.achievements.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {userData.achievements.map((achievement, index) => (
                    <div key={index} className="flex items-center p-3 bg-gray-50 rounded-md">
                      <div className="w-10 h-10 bg-[#E57373] rounded-full flex items-center justify-center mr-3">
                        <FaTrophy className="text-white" />
                      </div>
                      <div>
                        <p className="font-semibold">{achievement.title}</p>
                        <p className="text-sm text-gray-600">{achievement.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600">No achievements yet. Keep playing to earn some!</p>
              )}
            </div>

            {/* Sign Out Button */}
            <div className="text-center">
              <button
                onClick={handleSignOut}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-full px-8 py-3 text-lg transition duration-200"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Profile;